/**
 * Summary first, the full desk one press away.
 *
 * The desks are dense by design: a chief executive's decision with its
 * working, a cash position with every assumption beside it. That wins the
 * second hour and loses the first ten minutes. So a desk that publishes a
 * screen context — its three lines for a board, and the questions it can
 * answer — opens on those, with the full desk behind a button.
 *
 * Nothing is removed. The full desk is still rendered underneath, hidden, so
 * it keeps publishing the context Sky reads and a press shows it at once with
 * no reload. A screen that publishes no context is shown as it always was:
 * there is no summary to lead with, and inventing one would be worse.
 *
 * The choice is remembered in this browser: "Full desk" for this screen lasts
 * the visit; "Always open in full" lasts until changed back.
 */
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { getScreen, subscribeScreen } from "../assistant/screen";
import { useAssistant } from "../assistant/AssistantContext";
import { t } from "../i18n";
import s from "./DeskView.module.css";

type Mode = "summary" | "full";
const KEY = "sky.deskView";

function readMode(): Mode {
  try {
    return localStorage.getItem(KEY) === "full" ? "full" : "summary";
  } catch {
    return "summary";
  }
}

function writeMode(mode: Mode) {
  try {
    if (mode === "summary") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    /* storage unavailable: the choice lasts for this page */
  }
}

/* screens opened in full for this visit, by route */
const openedThisVisit = new Set<string>();

export function DeskView({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const ctx = useSyncExternalStore(subscribeScreen, getScreen, getScreen);
  const [mode, setMode] = useState<Mode>(readMode);
  const [, bump] = useState(0);

  const route = ctx?.route.split("?")[0];
  const here = !!ctx && route === pathname && ctx.summary.length > 0;
  /*
   * A desk that says it is worked at rather than read opens in full. Its
   * content is things to do — book a delivery in, hand parts over — and a
   * summary in front of those reads as a screen where the features are
   * missing. It still publishes its summary for Sky and the boards.
   */
  const summary = here && !ctx.worksInFull && mode === "summary" && !openedThisVisit.has(pathname);

  const openFull = () => {
    openedThisVisit.add(pathname);
    bump((n) => n + 1);
  };
  const backToSummary = () => {
    openedThisVisit.delete(pathname);
    if (mode === "full") {
      writeMode("summary");
      setMode("summary");
    }
    bump((n) => n + 1);
  };

  return (
    <>
      {summary && ctx && (
        <DeskSummary
          title={ctx.title}
          lines={ctx.summary}
          offers={ctx.offers}
          onOpen={openFull}
          onAlways={() => {
            writeMode("full");
            setMode("full");
          }}
        />
      )}
      {here && !summary && (
        <div className={s.bar}>
          <button type="button" className={s.barLink} onClick={backToSummary}>
            {t("Back to the summary")}
          </button>
        </div>
      )}
      <div hidden={summary}>{children}</div>
    </>
  );
}

function DeskSummary({
  title,
  lines,
  offers,
  onOpen,
  onAlways,
}: {
  title: string;
  lines: string[];
  offers: { label: string; ask: string }[];
  onOpen: () => void;
  onAlways: () => void;
}) {
  const assistant = useAssistant();
  const [first, ...rest] = lines;
  return (
    <section className={s.summary} aria-label={t("Summary")}>
      <div className="eyebrow">{t("At a glance")}</div>
      <h1 className={s.title}>{t(title)}</h1>
      {first && <p className={s.lead}>{t(first)}</p>}
      {rest.length > 0 && (
        <ul className={s.lines}>
          {rest.slice(0, 4).map((line) => (
            <li key={line}>{t(line)}</li>
          ))}
        </ul>
      )}
      {offers.length > 0 && (
        <div className={s.asks}>
          <span className={s.asksHead}>{t("Ask Sky")}</span>
          {offers.slice(0, 3).map((o) => (
            <button key={o.ask} type="button" className={s.ask} onClick={() => void assistant.ask(o.ask)}>
              {t(o.label)}
            </button>
          ))}
        </div>
      )}
      <div className={s.actions}>
        <button type="button" className={s.open} onClick={onOpen}>
          {t("Open the full desk")}
        </button>
        <button type="button" className={s.always} onClick={onAlways}>
          {t("Always open desks in full")}
        </button>
      </div>
    </section>
  );
}
